/**
 * Represents the `skip` and `take` configurations which are wrapped in the `page` object.
 */
export interface Page {
  /**
   * The number of records to skip.
   */
  skip: number;
  /**
   * The number of records to take.
   */
  take: number;
}

/**
 * @hidden
 */
export interface DataItemWrapper {
  dataItem: any;
  rowType: any;
  level: number;
  expanded: boolean;
  dataIndex: number;
}

/**
 * @hidden
 */
class RowHeightService {
  private offsets: Array<number> = [];
  private heights: Array<number> = [];

  constructor(private total: number = 0, rowHeight: number, detailRowHeight: number, data?: DataItemWrapper[]) {
    let agg = 0;
    for (let idx = 0; idx < total; idx++) {
      this.offsets.push(agg);
      const currHeight = (data && data[idx].expanded && data[idx].rowType === 'data') ? detailRowHeight : rowHeight;
      agg += currHeight;
      this.heights.push(currHeight);
    }
  }

  public height(rowIndex: number): number {
    return this.heights[rowIndex];
  }

  public index(position: number): number {
    for (let i = 0; i < this.offsets.length; i++) {
      if (position === this.offsets[i]) { 
        return i; 
      }
      if (position < this.offsets[i]) { 
        return i - 1; 
      }
    }
    return this.total - 1;
  }

  public offset(rowIndex: number): number {
    return this.offsets[rowIndex];
  }

  public totalHeight(): number {
    const lastOffset = this.offsets[this.offsets.length - 1];
    const lastHeight = this.heights[this.heights.length - 1];
    return lastOffset + lastHeight;
  }
}

import { memoizeOne } from '@progress/kendo-react-common';
import * as React from 'react';

const firefoxMaxHeight = 17895697;
const firefox = typeof window !== 'undefined' &&
  /Firefox/.test(window.navigator.userAgent);

/**
 * @hidden
 */
export class VirtualScrollStatic {
  public table: HTMLTableElement | null = null;

  public total = 0;
  public enabled = false;
  public skip = 0;
  public pageSize = 0;
  public PageChange: ((event: Page, syntheticEvent: React.SyntheticEvent<any>) => void) | null = null;

  public scrollElement: HTMLDivElement | null = null;

  public listTransform: string = '';

  public itemHeight = 0;
  public containerHeight = 0;

  public rowHeightService?: RowHeightService;
  private reactVersion: number = Number.parseFloat(React.version);
  private scrollSyncing: boolean = false;
  private lastLoaded: number = 0;
  private firstLoaded: number = 0;
  private lastScrollTop: number = 0;

  private listTranslate = 0;
  public get translate() {
    return this.listTranslate;
  }

  public list: HTMLUListElement | null = null;

  public container: HTMLDivElement | null = null;

  constructor() {
    this.firstLoaded = this.pageSize;
    this.lastLoaded = this.skip + this.pageSize;

    this.scrollHandler = this.scrollHandler.bind(this);
  }

  translateTo(dY: number, force?: boolean) {
    this.listTranslate = dY;
    if (this.enabled && this.list) {
      if (firefox || this.reactVersion <= 17 || force) {
        this.list.style.transform = 'translateY(' + dY + 'px)';
      } else {
        this.listTransform = 'translateY(' + dY + 'px)';
      }
    }
  }

  changePage(skip: number, e: React.SyntheticEvent<HTMLDivElement>): void {
    const newSkip = Math.min(Math.max(0, skip), this.total - this.pageSize);

    if (newSkip !== this.skip && this.PageChange) {
      this.PageChange({ skip: newSkip, take: this.pageSize }, e);
    }
  }

  reset() {
    if (this.container) {
      this.calcScrollElementHeight();
      this.container.scrollTop = 0;
      this.translateTo(0, true);
    }
  }

  scrollToEnd() {
    if (this.container && this.list) {
      this.calcScrollElementHeight();
      const { scrollHeight, offsetHeight } = this.container;
      this.container.scrollTop = scrollHeight;
      this.translateTo(scrollHeight - offsetHeight, true);
    }
  }

  calcScrollElementHeight = () => {
    this.scrollSyncing = true;
    let heightChanged = false;

    const liCssRules = this.list ? window.getComputedStyle((this.list.children[0] as HTMLLIElement), null) : null;
    const marginBottom = liCssRules ? parseInt(liCssRules.marginBottom) : 0;
    this.itemHeight = this.list ? (this.list.children[0] as HTMLLIElement).offsetHeight + marginBottom : this.itemHeight;
    let maxHeight = this.itemHeight * this.total - (this.list?.offsetHeight || 0);
    if (maxHeight < 0) {
      maxHeight = 0;
    }
    this.containerHeight = firefox ? Math.min(firefoxMaxHeight, maxHeight) : maxHeight;

    const newHeight = this.containerHeight;

    if (this.scrollElement) {
      heightChanged = this.scrollElement.style.height !== newHeight + 'px';
      if (heightChanged) {
        this.scrollElement.style.height = newHeight + 'px';
      }
    }

    this.scrollSyncing = false;

    return heightChanged;
  };

  scrollerRef = (element: HTMLDivElement | null): void => {
    this.container = element;
    if (element) {
      element.setAttribute('unselectable', 'on');
      window.setTimeout(this.calcScrollElementHeight.bind(this), 0);
    }
  };

  scrollHandler(e: React.SyntheticEvent<HTMLDivElement>) {
    if (!this.enabled || !this.container || !this.list) {
      return;
    }

    if (this.scrollSyncing) {
      this.scrollSyncing = false;
      return;
    }

    this.rowHeightService = this.getRowHeightService(this.itemHeight, this.total);

    const scrollTop = this.container.scrollTop;
    const up = this.lastScrollTop >= scrollTop;
    const down = !up;
    const scrolled = Math.abs(scrollTop - this.lastScrollTop);
    this.lastScrollTop = scrollTop;

    let firstItemIndex = this.rowHeightService.index(scrollTop)!;
    let firstItemOffset = this.rowHeightService.offset(firstItemIndex);

    const { offsetHeight } = this.container;

    const lastItemIndex = this.rowHeightService.index(scrollTop + offsetHeight)!;

    if (down && lastItemIndex >= this.lastLoaded && this.lastLoaded < this.total) {
      const overflow = (firstItemIndex + this.pageSize) - this.total;

      if (overflow > 0) {
        firstItemIndex = firstItemIndex - overflow;
        firstItemOffset = this.rowHeightService.offset(firstItemIndex);
      }

      this.firstLoaded = firstItemIndex;
      this.translateTo(firstItemOffset);

      const nextTake = this.firstLoaded + this.pageSize;
      this.lastLoaded = Math.min(nextTake, this.total);

      this.changePage(this.firstLoaded, e);
    } else if (up && firstItemIndex - 1 <= this.firstLoaded) {
      const upArrow = scrolled === this.itemHeight;
      if (this.skip === 0 && upArrow) {
        this.lastScrollTop = 0;
        this.firstLoaded = 0;
        this.lastLoaded = this.pageSize;
        this.container.scrollTop = 0;
        this.translateTo(0, true);
      } else {
        const nonVisibleBuffer = Math.floor(this.pageSize * 0.3);

        this.firstLoaded = Math.max(firstItemIndex - nonVisibleBuffer, 0);
        this.translateTo(this.rowHeightService.offset(this.firstLoaded));

        this.lastLoaded = Math.min(this.firstLoaded + this.pageSize, this.total);
        this.changePage(this.firstLoaded, e);
      }
    }
  }

  private getRowHeightService = memoizeOne((itemHeight: number, total: number) => new RowHeightService(total, itemHeight, 0));
}
